import * as React from "react";
import { Icon } from "../actions/Icon";
import { Switch } from "./Switch";

/**
 * Settings row: icon, label, description and a Switch (or any trailing control). Rows divide themselves with a hairline.
 * `switchPosition="start"` puts the Switch where the icon goes, leading the row (the icon is not drawn).
 */
export interface ToggleRowProps {
  icon?: string;
  iconColor?: string;
  label: React.ReactNode;
  description?: React.ReactNode;
  checked?: boolean;
  defaultChecked?: boolean;
  onChange?: (checked: boolean) => void;
  /** Dims the row when a provider can't do this feature */
  unavailable?: boolean;
  /** Replaces the Switch */
  trailing?: React.ReactNode;
  /** "start" leads the row with the Switch in place of the icon. Default "end". Ignored with `trailing`. */
  switchPosition?: "start" | "end";
  divided?: boolean;
  className?: string;
}

export function ToggleRow({
  icon,
  iconColor,
  label,
  description,
  checked,
  defaultChecked,
  onChange,
  unavailable,
  trailing,
  switchPosition = "end",
  divided,
  className,
}: ToggleRowProps): JSX.Element {
  const cls = [
    "gk-togglerow",
    unavailable && "gk-togglerow--unavailable",
    divided && "gk-togglerow--divided",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  const leading = switchPosition === "start" && trailing === undefined;
  const control =
    trailing !== undefined ? (
      trailing
    ) : (
      <Switch
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={onChange}
        disabled={unavailable}
        label={typeof label === "string" ? label : undefined}
      />
    );
  return (
    <div className={cls}>
      {leading
        ? control
        : icon && (
            <Icon name={icon} className="gk-togglerow__icon" style={iconColor ? { color: iconColor } : undefined} />
          )}
      <div className="gk-togglerow__body">
        <div className="gk-togglerow__label">{label}</div>
        {description && <div className="gk-togglerow__desc">{description}</div>}
      </div>
      {leading ? null : control}
    </div>
  );
}
